import { supabase } from '../lib/supabase';
import { attachmentService } from './attachmentService';

const BASE = 'id, owner_id, title, body, occurred_at, created_at, updated_at, origin_id, origin_note, origin:moment_origins(id, label)';
const FIELDS = `${BASE}, moment_contacts(contact_id, contacts(id, full_name, company)), moment_attachments(id, kind, storage_path, file_name, mime_type, size_bytes, duration_seconds, created_at)`;

const toPayload = (values) => {
  const title = (values.title ?? '').trim();
  const note = (values.origin_note ?? '').trim();
  const originId = values.origin_id || null;
  return {
    title: title === '' ? null : title,
    body: (values.body ?? '').trim(),
    occurred_at: values.occurred_at,
    origin_id: originId,
    origin_note: originId && note ? note : null
  };
};

const withPeople = (row) => {
  const { moment_contacts: links, moment_attachments: files, filter_link: unusedFilter, ...rest } = row;
  return {
    ...rest,
    people: (links || []).map((l) => l.contacts).filter(Boolean),
    attachments: (files || []).slice().sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
  };
};

// Keeps characters that could break a PostgREST filter string out of search words.
const searchWords = (q) =>
  (q || '').split(/\s+/).map((w) => w.replace(/[%_,()*\\"']/g, '')).filter(Boolean).slice(0, 6);

const startOfDay = (d) => new Date(`${d}T00:00:00`).toISOString();
const endOfDay = (d) => new Date(`${d}T23:59:59.999`).toISOString();

const SORTS = {
  newest: ['occurred_at', false],
  oldest: ['occurred_at', true],
  updated: ['updated_at', false]
};

export const momentService = {
  async getMoments() {
    const { data, error } = await supabase
      .from('moments')
      .select(FIELDS)
      .order('occurred_at', { ascending: false });
    if (error) throw error;
    return (data || []).map(withPeople);
  },

  // Database-backed search over the caller's live Moments. Trashed Moments are hidden by RLS.
  // filters: { q, contactId, originId, from, to, sort }
  async searchMoments(filters = {}) {
    const personFilter = !!filters.contactId;
    let query = supabase
      .from('moments')
      .select(personFilter ? `${FIELDS}, filter_link:moment_contacts!inner(contact_id)` : FIELDS);
    searchWords(filters.q).forEach((w) => {
      query = query.or(`title.ilike.*${w}*,body.ilike.*${w}*,origin_note.ilike.*${w}*`);
    });
    if (personFilter) query = query.eq('filter_link.contact_id', filters.contactId);
    if (filters.originId) query = query.eq('origin_id', filters.originId);
    if (filters.from) query = query.gte('occurred_at', startOfDay(filters.from));
    if (filters.to) query = query.lte('occurred_at', endOfDay(filters.to));
    const [column, ascending] = SORTS[filters.sort] || SORTS.newest;
    const { data, error } = await query.order(column, { ascending }).limit(200);
    if (error) throw error;
    return (data || []).map(withPeople);
  },

  // All Moments linked to one Contact (the same underlying Moment rows as the main timeline).
  async getMomentsForContact(contactId) {
    const { data: links, error: linkError } = await supabase
      .from('moment_contacts')
      .select('moment_id')
      .eq('contact_id', contactId);
    if (linkError) throw linkError;
    const ids = (links || []).map((l) => l.moment_id);
    if (!ids.length) return [];
    const { data, error } = await supabase
      .from('moments')
      .select(FIELDS)
      .in('id', ids)
      .order('occurred_at', { ascending: false });
    if (error) throw error;
    return (data || []).map(withPeople);
  },

  async getMoment(id) {
    const { data, error } = await supabase.from('moments').select(FIELDS).eq('id', id).single();
    if (error) throw error;
    return withPeople(data);
  },

  async createMoment(ownerId, values) {
    const { data, error } = await supabase
      .from('moments')
      .insert({ owner_id: ownerId, ...toPayload(values) })
      .select(BASE)
      .single();
    if (error) throw error;
    return { ...data, people: [], attachments: [] };
  },

  async updateMoment(id, values) {
    const { data, error } = await supabase
      .from('moments')
      .update(toPayload(values))
      .eq('id', id)
      .select(BASE)
      .single();
    if (error) throw error;
    return data;
  },

  // Makes the Moment's linked people exactly match contactIds. Never touches the Moment or Contacts.
  async setPeople(momentId, ownerId, contactIds) {
    const wanted = Array.from(new Set(contactIds));
    const { data: existing, error: readError } = await supabase
      .from('moment_contacts')
      .select('contact_id')
      .eq('moment_id', momentId);
    if (readError) throw readError;
    const have = (existing || []).map((r) => r.contact_id);
    const toRemove = have.filter((id) => !wanted.includes(id));
    const toAdd = wanted.filter((id) => !have.includes(id));
    if (toRemove.length) {
      const { error } = await supabase
        .from('moment_contacts')
        .delete()
        .eq('moment_id', momentId)
        .in('contact_id', toRemove);
      if (error) throw error;
    }
    if (toAdd.length) {
      const rows = toAdd.map((contact_id) => ({ moment_id: momentId, contact_id, owner_id: ownerId }));
      const { error } = await supabase.from('moment_contacts').insert(rows);
      if (error) throw error;
    }
  },

  // Immediate removal. Used only to roll back a Moment that was just created and has no content.
  async deleteMoment(id) {
    const { data: files } = await supabase
      .from('moment_attachments')
      .select('storage_path')
      .eq('moment_id', id);
    const { error } = await supabase.from('moments').delete().eq('id', id);
    if (error) throw error;
    await attachmentService.removeObjects((files || []).map((f) => f.storage_path));
  },

  // Moves a Moment to Trash for 30 days. Files, people and attachments are left untouched.
  async trashMoment(id) {
    const { error } = await supabase.rpc('trash_moment', { p_moment_id: id });
    if (error) throw error;
  },

  async listTrash() {
    const { data, error } = await supabase.rpc('list_trashed_moments');
    if (error) throw error;
    return (data || []).map((r) => ({
      id: r.o_id,
      title: r.o_title,
      body: r.o_body,
      occurred_at: r.o_occurred_at,
      origin_label: r.o_origin_label,
      origin_note: r.o_origin_note,
      deleted_at: r.o_deleted_at,
      purge_after: r.o_purge_after,
      people: r.o_people || [],
      attachment_count: r.o_attachment_count || 0
    }));
  },

  async restoreMoment(id) {
    const { error } = await supabase.rpc('restore_trashed_moment', { p_moment_id: id });
    if (error) throw error;
  },

  // Files first; the database row is removed only if every file was removed.
  async deletePermanently(id) {
    const { data, error } = await supabase.rpc('trashed_moment_files', { p_moment_id: id });
    if (error) throw error;
    const paths = (data || []).map((r) => (typeof r === 'string' ? r : Object.values(r)[0])).filter(Boolean);
    await attachmentService.removeObjectsStrict(paths);
    const { error: delError } = await supabase.rpc('delete_trashed_moment', { p_moment_id: id });
    if (delError) throw delError;
  }
};